import { IComparable, Dictionary, Iterable, ObjUtils, IEquatable, HashSet } from 'DiscoveryEngine.TS/Utils';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';

export class Privilege implements IComparable, IEquatable {

	public static readonly PortsEnableDisable = new Privilege("ports.enable.disable", "Enable/Disable Ports");
	public static readonly PortsManagePoe = new Privilege("ports.manage.poe", "Manage Port POE");
	public static readonly PortsManageAccessVlans = new Privilege("ports.manage.access.vlans", "Manage Port Access VLANs");
	public static readonly PortsEditAliases = new Privilege("ports.edit.aliases", "Edit Port Aliases");
	public static readonly ManageTrunkedVlans = new Privilege("manage.trunked.vlans", "Manage Trunked VLANs");
	public static readonly ViewConfigs = new Privilege("configs.view", "View/Compare Configs");
	public static readonly ManageAgents = new Privilege("manage.agents", "Manage Agents");

	private static Map: Dictionary<string, Privilege> = new Dictionary<string, Privilege>();

	private static staticConstructor = (() => {
		Privilege.Map.Add(Privilege.PortsEnableDisable.Name, Privilege.PortsEnableDisable);
		Privilege.Map.Add(Privilege.PortsManagePoe.Name, Privilege.PortsManagePoe);
		Privilege.Map.Add(Privilege.PortsManageAccessVlans.Name, Privilege.PortsManageAccessVlans);
		Privilege.Map.Add(Privilege.PortsEditAliases.Name, Privilege.PortsEditAliases);
		Privilege.Map.Add(Privilege.ManageTrunkedVlans.Name, Privilege.ManageTrunkedVlans);
		Privilege.Map.Add(Privilege.ViewConfigs.Name, Privilege.ViewConfigs);
		Privilege.Map.Add(Privilege.ManageAgents.Name, Privilege.ManageAgents);
	})();

	public static Get(name: string): Privilege {
		let result: Privilege = null;
		if (Privilege.Map.ContainsKey(name)) {
			result = Privilege.Map.Item(name);
		}
		console.assert(!!result);
		return result;
	}

	public static GetAll(): Iterable<Privilege> {
		return Privilege.Map.Values();
	}


	public Name: string;
	public Label: string;

	private constructor(name: string, label: string) {
		this.Name = name;
		this.Label = label;
	}

	public ToString(): string {
		return this.Name + " - " + this.Label;
	}

	public Equals(obj: any): boolean {
		if (!obj) {
			return false;
		}
		if (obj === this) {
			return true;
		}
		if (!(obj instanceof Privilege)) {
			return false;
		}
		let other: Privilege = (obj as Privilege);

		return (ObjUtils.Equals(this.Name, other.Name) && ObjUtils.Equals(this.Label, other.Label));
	}

	public GetHashCode(): number {
		return (ObjUtils.GetHashCode(this.Name) ^ ObjUtils.GetHashCode(this.Label));
	}

	public CompareTo(obj: any): number {
		if (!(obj instanceof Privilege)) {
			return 1;
		}

		let other: Privilege = (obj as Privilege);

		let result: number = ObjUtils.CompareTo(this.Label, other.Label)
		if (result === 0) {
			result = ObjUtils.CompareTo(this.Name, other.Name);
		}
		return result;
	}
}


export class Privileges {

	private Entries: HashSet<Privilege>;

	constructor() {
		this.Entries = new HashSet<Privilege>();
	}

	public get HasEntries(): boolean {
		return (!!this.Entries && this.Entries.Count > 0);
	}

	public Clear(): void {
		this.Entries.Clear();
	}

	public Add(privilege: Privilege): void {
		this.Entries.Add(privilege);
	}

	public Remove(privilege: Privilege): void {
		this.Entries.Remove(privilege);
	}

	public Contains(privilege: Privilege): boolean {
		return this.Entries.Contains(privilege);
	}

	public Union(privileges: Privileges): void {
		privileges.Entries.ForEach((p: Privilege) => this.Add(p));
	}

	public Equals(obj: any): boolean {
		if (!obj) {
			return false;
		}
		if (obj === this) {
			return true;
		}
		if (!(obj instanceof Privileges)) {
			return false;
		}

		let other: Privileges = (obj as Privileges);

		return ObjUtils.SetEquals(this.Entries, other.Entries);
	}

	public static readonly ObjSer_ObjectName: string = "Privileges_ObjectName";

	public Write(obj: any, context: any): void {

		let objName = "privileges";
		if (context[Privileges.ObjSer_ObjectName] !== undefined)
			objName = context[Privileges.ObjSer_ObjectName];

		obj[objName] = {};
		obj = obj[objName];

		if (this.HasEntries) {
			obj.entries = [];
			ObjSerUtils.WriteCollection(obj.entries, this.Entries, (entry: Privilege) => {
				return {
					privilege: {
						name: entry.Name,
					}
				};
			}, context);
		}
	}

	public Read(obj: any, context: any): void {

		let objName = "privileges";
		if (context[Privileges.ObjSer_ObjectName] !== undefined)
			objName = context[Privileges.ObjSer_ObjectName];

		if (obj[objName] === undefined)
			throw new Error();
		obj = obj[objName];

		if (obj.entries !== undefined) {
			this.Entries.Clear();
			obj.entries.forEach((elem: any) => {
				let privilege = Privilege.Get(elem.privilege.name);
				this.Add(privilege);
			}, this);
		}
	}
}